import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Phone, Mail } from "lucide-react"; // ✅ SVG icons

import DropdownMenu from "./DropdownMenu";
import MobileDropdown from "./MobileDropdown";
import logo from "../../assets/winglo.png";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  const toggleDropdown = (menu) => {
    setOpenDropdown((prev) => (prev === menu ? null : menu));
  };

  const handleLinkClick = () => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
    setMobileProductsOpen(false);
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-6 py-3 max-w-screen-xl mx-auto relative">

          {/* LEFT - LOGO */}
          <div className="flex-shrink-0">
            <Link to="/" onClick={handleLinkClick}>
              <img src={logo} className="h-14" alt="logo" />
            </Link>
          </div>

          {/* CENTER - NAV */}
          <nav
            ref={navRef}
            className="hidden lg:flex gap-6 text-sm font-medium absolute left-1/2 transform -translate-x-1/2"
          >
            <Link to="/" onClick={handleLinkClick} className={`${isActive("/") ? "text-[#FF7A00]" : ""} hover:text-[#FF7A00]`}>
              Home
            </Link>

            <Link to="/about" onClick={handleLinkClick} className={`${isActive("/about") ? "text-[#FF7A00]" : ""} hover:text-[#FF7A00]`}>
              About
            </Link>

            <DropdownMenu
              title="Products"
              openDropdown={openDropdown}
              toggleDropdown={toggleDropdown}
              handleLinkClick={handleLinkClick}
              isActive={isActive}
            />

            <Link to="/clients" onClick={handleLinkClick} className={`${isActive("/clients") ? "text-[#FF7A00]" : ""} hover:text-[#FF7A00]`}>
              Clients
            </Link>

            <Link to="/contact" onClick={handleLinkClick} className={`${isActive("/contact") ? "text-[#FF7A00]" : ""} hover:text-[#FF7A00]`}>
              Contact
            </Link>
          </nav>

          {/* RIGHT - CONTACT */}
          <div className="flex items-center gap-4">

            {/* 📱 MOBILE GREEN ICONS */}
            <div className="flex lg:hidden items-center gap-4">

              <a href="tel:9842009463">
                <Phone className="w-4 h-4 text-blue-600" />
              </a>

              <a href="mailto:contact@winglolive.com">
                <Mail className="w-4 h-4 text-blue-600" />
              </a>

            </div>

            {/* 💻 DESKTOP CONTACT */}
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <a href="tel:9842009463" className="flex items-center gap-2 hover:text-blue-600">
                <Phone className="w-4 h-4 text-blue-600" />
                9842009463
              </a>

              <a href="mailto:contact@winglolive.com" className="flex items-center gap-2 hover:text-blue-600">
                <Mail className="w-4 h-4 text-blue-600" />
                contact@winglolive.com
              </a>
            </div>

            {/* MOBILE MENU BUTTON */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-xl"
            >
              ☰
            </button>

          </div>
        </div>

       {isMobile && isMobileMenuOpen && (
  <div className="fixed top-0 left-0 w-full h-full bg-white z-50 p-6 text-center overflow-y-auto">

    {/* Top */}
    <div className="flex justify-between mb-6">
      <img src={logo} className="w-[120px]" alt="logo" />
      <button onClick={() => setIsMobileMenuOpen(false)}>✕</button>
    </div>

    {/* Menu */}
    <div className="space-y-6 text-lg">

      {/* Home */}
      <div
        onClick={() => { navigate("/"); handleLinkClick(); }}
        className={`
          cursor-pointer px-2 py-1 rounded-md transition
          ${isActive("/") ? "text-[#FF7A00] font-semibold" : "text-black"}
          hover:text-[#FF7A00]
        `}
      >
        Home
      </div>

      {/* About */}
      <div
        onClick={() => { navigate("/about"); handleLinkClick(); }}
        className={`
          cursor-pointer px-2 py-1 rounded-md transition
          ${isActive("/about") ? "text-[#FF7A00] font-semibold" : "text-black"}
          hover:text-[#FF7A00]
        `}
      >
        About
      </div>

      {/* Products */}
  
     
              {/* ✅ MOBILE DROPDOWN */}
              <MobileDropdown
                title="Products"
                isOpen={mobileProductsOpen}
                setIsOpen={setMobileProductsOpen}
                handleLinkClick={handleLinkClick}
              />

      {/* Clients */}
      <div
        onClick={() => { navigate("/clients"); handleLinkClick(); }}
        className={`
          cursor-pointer px-2 py-1 rounded-md transition
          ${isActive("/clients") ? "text-[#FF7A00] font-semibold" : "text-black"}
          hover:text-[#FF7A00]
        `}
      >
        Clients
      </div>

      {/* Contact */}
      <div
        onClick={() => { navigate("/contact"); handleLinkClick(); }}
        className={`
          cursor-pointer px-2 py-1 rounded-md transition
          ${isActive("/contact") ? "text-[#FF7A00] font-semibold" : "text-black"}
          hover:text-[#FF7A00]
        `}
      >
        Contact
      </div>

    </div>
  </div>
)}
      </header>
    </>
  );
};

export default Header;